/**
 * DashboardMode - Full-page executive summary dashboard
 * Shows health overview, journey progress, attention points, and key stats
 */

import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useWorkspaceStore, type WorkspaceMode } from '@/stores/workspaceStore'
import { useJourneyStore } from '@/stores/journeyStore'
import { useSessionStore } from '@/stores/sessionStore'
import { useConnectionStore } from '@/stores/connectionStore'
import { sourcesApi } from '@/lib/api/client'
import { getOntologyStats } from '@/lib/sparql/queries'
import type { OntologyDomain } from '@/types/ontology'
import {
  ChevronDown,
  AlertCircle,
  AlertTriangle,
  Info,
  FileText,
  Box,
  TrendingUp,
  Target,
  CheckCircle2,
  Circle,
  Loader2,
  Sprout,
  Search,
  Map as MapIcon,
  Rocket,
  Settings2,
} from 'lucide-react'
import type { JourneyPhase } from '@ispot/shared'

// Goal ID to icon mapping
const goalIcons: Record<string, React.ReactNode> = {
  'validate-new-idea': <Sprout className="w-5 h-5" />,
  'diagnose-stalled-growth': <Search className="w-5 h-5" />,
  'sharpen-positioning': <Target className="w-5 h-5" />,
  'prioritize-roadmap': <MapIcon className="w-5 h-5" />,
  'expand-new-market': <Rocket className="w-5 h-5" />,
}
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

interface AttentionPoint {
  type: 'critical' | 'warning' | 'info'
  message: string
}

interface DashboardModeProps {
  context: {
    mode: WorkspaceMode
    phaseId: string
    journeyMode: 'structured' | 'free'
  }
  onOpenGoalWizard?: () => void
}

export function DashboardMode({ onOpenGoalWizard }: DashboardModeProps) {
  const { setActiveMode } = useWorkspaceStore()
  const { accountId, projectId, isReady } = useSessionStore()
  const { isConnected } = useConnectionStore()
  const {
    journey,
    isLoading,
    fetchJourney,
    getCurrentPhase,
    getDomainScores,
    getOverallProgress,
    getPhaseProgress,
    isActivityCompleted,
    isArtifactCompleted,
    getSelectedGoal,
    getActivePhases,
  } = useJourneyStore()

  const [expandedSections, setExpandedSections] = useState({
    attention: true,
    phases: true,
  })
  const [expandedPhases, setExpandedPhases] = useState<Set<string>>(new Set())

  // Fetch journey data on mount
  useEffect(() => {
    if (accountId && projectId) {
      fetchJourney(accountId, projectId)
    }
  }, [accountId, projectId, fetchJourney])

  // Fetch document count
  const { data: sources = [] } = useQuery({
    queryKey: ['sources', accountId, projectId],
    queryFn: () => sourcesApi.list(accountId!, projectId!),
    enabled: isReady,
    staleTime: 60000,
  })

  // Fetch entity stats from Fuseki
  const { data: ontologyStats } = useQuery({
    queryKey: ['ontologyStats'],
    queryFn: getOntologyStats,
    enabled: isConnected,
    staleTime: 60000,
  })

  // Get computed values from store
  const phases = getActivePhases()
  const domainScores = getDomainScores()
  const overallProgress = getOverallProgress()
  const currentPhase = getCurrentPhase()
  const selectedGoal = getSelectedGoal()
  const attentionPoints = getAttentionPoints(
    overallProgress,
    journey?.currentPhaseId || '',
    journey?.completedActivities || [],
    journey?.phaseProgress || {}
  )

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }))
  }

  const togglePhase = (phaseId: string) => {
    setExpandedPhases((prev) => {
      const next = new Set(prev)
      if (next.has(phaseId)) {
        next.delete(phaseId)
      } else {
        next.add(phaseId)
      }
      return next
    })
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-warm-50">
        <div className="text-center">
          <Loader2 size={32} className="animate-spin text-business mx-auto mb-3" />
          <p className="text-sm text-text-muted">Loading journey...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto bg-warm-50 p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Page Header */}
        <div className="mb-8">
          <h2 className="text-2xl font-display font-bold text-warm-900">Performance Dashboard</h2>
          <p className="text-sm text-text-muted mt-1">Executive summary of your product discovery progress</p>
        </div>

        {/* Row 1: Health Score + Journey Goal side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Health Score Card */}
          <div className="bg-white rounded-xl border border-warm-200 p-6 shadow-sm">
            <div className="flex items-center gap-6">
              {/* Three-Ring Health Gauge */}
              <DomainHealthRings
                market={domainScores.market}
                solution={domainScores.solution}
                business={domainScores.business}
                overall={overallProgress}
              />

              {/* Health Info */}
              <div className="flex-1 min-w-0">
                <div className="text-xs text-text-muted mb-1">Current Phase</div>
                <div className="text-lg font-semibold mb-3 truncate" style={{ color: currentPhase?.color || '#5c8a6b' }}>
                  {currentPhase ? `${currentPhase.order}. ${currentPhase.name}` : 'Not Started'}
                </div>

                {/* Domain Legend */}
                <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-warm-100">
                  <DomainLegendItem color="var(--color-market)" label="Market" value={domainScores.market} />
                  <DomainLegendItem color="var(--color-solution)" label="Solution" value={domainScores.solution} />
                  <DomainLegendItem color="var(--color-business)" label="Business" value={domainScores.business} />
                </div>
              </div>
            </div>
          </div>

          {/* Journey Goal Card */}
          <div className="bg-white rounded-xl border border-warm-200 p-6 shadow-sm">
            <div className="flex items-start justify-between h-full">
              <div className="flex items-start gap-4">
                {/* Goal Icon */}
                <div className={cn(
                  'w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0',
                  selectedGoal ? 'bg-market/20 text-market' : 'bg-warm-100 text-text-muted'
                )}>
                  {selectedGoal ? (goalIcons[selectedGoal.id] || <Target className="w-6 h-6" />) : <Target className="w-6 h-6" />}
                </div>

                {/* Goal Info */}
                <div className="min-w-0">
                  <div className="text-xs text-text-muted mb-0.5">Journey Goal</div>
                  {selectedGoal ? (
                    <>
                      <div className="font-semibold text-warm-900">{selectedGoal.name}</div>
                      <div className="text-sm text-text-secondary mt-1 line-clamp-2">
                        {selectedGoal.shortDescription || selectedGoal.description}
                      </div>
                      <div className="flex items-center gap-2 mt-3">
                        <span className={cn(
                          'text-xs px-2 py-0.5 rounded-full',
                          selectedGoal.type === 'greenfield'
                            ? 'bg-market/10 text-market'
                            : 'bg-business/10 text-business'
                        )}>
                          {selectedGoal.type === 'greenfield' ? 'New Product' : 'Existing Product'}
                        </span>
                        <span className="text-xs text-text-muted">
                          {Object.values(selectedGoal.phaseConfig).filter(m => m !== 'skip').length} phases
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="font-semibold text-warm-900">No Goal Selected</div>
                      <div className="text-sm text-text-secondary mt-1">
                        Choose a goal to customize your discovery journey
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Change Goal Button */}
              {onOpenGoalWizard && (
                <button
                  onClick={onOpenGoalWizard}
                  className={cn(
                    'flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex-shrink-0',
                    selectedGoal
                      ? 'text-text-secondary hover:text-text hover:bg-warm-100'
                      : 'bg-market text-white hover:bg-market/90'
                  )}
                >
                  {selectedGoal ? (
                    <>
                      <Settings2 size={14} />
                      Change
                    </>
                  ) : (
                    'Select Goal'
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Row 2: Stats + Attention Points side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Stats Card */}
          <div className="bg-white rounded-xl border border-warm-200 p-6 shadow-sm">
            <div className="flex items-center gap-2 text-sm font-medium text-warm-900 mb-4">
              <TrendingUp size={16} className="text-text-secondary" />
              Key Statistics
            </div>
            <div className="grid grid-cols-2 gap-4">
              <StatItem icon={<FileText size={16} />} label="Documents" value={sources.length} />
              <StatItem
                icon={<Box size={16} />}
                label="Entities"
                value={ontologyStats
                  ? ontologyStats.instancesByDomain.market + ontologyStats.instancesByDomain.business + ontologyStats.instancesByDomain.solution
                  : 0}
              />
            </div>
            {/* Entities by Domain */}
            {ontologyStats && (
              <div className="mt-4 pt-4 border-t border-warm-100">
                <div className="text-xs text-text-muted mb-3">Entities by Domain</div>
                <div className="flex flex-wrap gap-2">
                  <DomainEntityBadge domain="market" count={ontologyStats.instancesByDomain.market} />
                  <DomainEntityBadge domain="business" count={ontologyStats.instancesByDomain.business} />
                  <DomainEntityBadge domain="solution" count={ontologyStats.instancesByDomain.solution} />
                </div>
              </div>
            )}
          </div>

          {/* Attention Points Card */}
          <div className="bg-white rounded-xl border border-warm-200 p-6 shadow-sm">
            <div className="flex items-center gap-2 text-sm font-medium text-warm-900 mb-4">
              <AlertCircle size={16} className="text-error" />
              Attention Points
              {attentionPoints.length > 0 && (
                <span className="px-2 py-0.5 text-xs font-medium text-white rounded-full bg-error">
                  {attentionPoints.length}
                </span>
              )}
            </div>
            {attentionPoints.length === 0 ? (
              <div className="flex items-center gap-3 text-sm text-text-muted">
                <CheckCircle2 size={18} className="text-success" />
                No issues requiring attention
              </div>
            ) : (
              <div className="space-y-2">
                {attentionPoints.slice(0, 3).map((point, idx) => (
                  <AttentionCard key={idx} type={point.type} message={point.message} compact />
                ))}
                {attentionPoints.length > 3 && (
                  <div className="text-xs text-text-muted pt-1">+{attentionPoints.length - 3} more</div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Horizontal Phase Timeline */}
        <div className="bg-white rounded-xl border border-warm-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-warm-100">
            <div className="flex items-center gap-2 text-sm font-medium text-warm-900">
              <Target size={16} className="text-business" />
              Journey Phases
            </div>
          </div>

          {/* Timeline Container */}
          <div className="p-6">
            {/* Horizontal Timeline */}
            <div className="relative">
              {/* Connecting Line */}
              <div className="absolute top-7 left-6 right-6 h-0.5 bg-warm-200" />

              {/* Phase Nodes */}
              <div className="relative flex justify-between">
                {phases.map((phase, index) => {
                  const progress = getPhaseProgress(phase.id)
                  const isExpanded = expandedPhases.has(phase.id)
                  const isCurrent = phase.id === journey?.currentPhaseId
                  const isCompleted = progress >= 100

                  return (
                    <button
                      key={phase.id}
                      onClick={() => togglePhase(phase.id)}
                      className="flex flex-col items-center group"
                      style={{ width: `${100 / phases.length}%` }}
                    >
                      {/* Phase Node with Progress Ring */}
                      <div className="relative">
                        {/* Progress Ring SVG */}
                        <svg
                          className="w-14 h-14 -rotate-90"
                          viewBox="0 0 56 56"
                        >
                          {/* Background circle */}
                          <circle
                            cx="28"
                            cy="28"
                            r="24"
                            fill="none"
                            stroke="var(--color-warm-200)"
                            strokeWidth="4"
                          />
                          {/* Progress circle */}
                          <circle
                            cx="28"
                            cy="28"
                            r="24"
                            fill="none"
                            stroke={phase.color}
                            strokeWidth="4"
                            strokeLinecap="round"
                            strokeDasharray={`${(progress / 100) * 2 * Math.PI * 24} ${2 * Math.PI * 24}`}
                            style={{ transition: 'stroke-dasharray 0.4s ease-in-out' }}
                          />
                        </svg>
                        {/* Inner circle with number */}
                        <div
                          className={cn(
                            'absolute inset-0 m-auto w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold transition-all',
                            'shadow-sm group-hover:scale-110'
                          )}
                          style={{ backgroundColor: phase.color }}
                        >
                          {isCompleted ? (
                            <CheckCircle2 size={18} />
                          ) : (
                            phase.order
                          )}
                        </div>
                      </div>

                      {/* Phase Label */}
                      <div className="mt-3 text-center px-1">
                        <div className={cn(
                          'text-xs font-medium truncate max-w-[100px]',
                          isCurrent ? 'text-warm-900' : 'text-text-secondary'
                        )}>
                          {phase.name}
                        </div>
                        <div className="text-[10px] text-text-muted mt-0.5">
                          {Math.round(progress)}%
                        </div>
                      </div>

                      {/* Current Indicator */}
                      {isCurrent && (
                        <div className="mt-1">
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0" style={{ borderColor: phase.color, color: phase.color }}>
                            Current
                          </Badge>
                        </div>
                      )}

                      {/* Expand Indicator */}
                      <div className={cn(
                        'mt-2 transition-transform',
                        isExpanded ? 'rotate-180' : ''
                      )}>
                        <ChevronDown size={14} className="text-text-muted" />
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Expanded Phase Details */}
            {phases.map((phase) => {
              const isExpanded = expandedPhases.has(phase.id)
              if (!isExpanded) return null

              return (
                <div
                  key={`${phase.id}-details`}
                  className="mt-6 p-4 rounded-lg border border-warm-200 bg-warm-50/50"
                >
                  <div className="flex items-start gap-3 mb-4">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0"
                      style={{ backgroundColor: phase.color }}
                    >
                      {phase.order}
                    </div>
                    <div>
                      <div className="font-medium text-warm-900">{phase.name}</div>
                      <div className="text-sm text-text-secondary mt-1">{phase.description}</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Activities */}
                    <div>
                      <h4 className="text-xs font-medium text-text-muted uppercase tracking-wide mb-2">
                        Activities ({phase.activities.length})
                      </h4>
                      <div className="space-y-2">
                        {phase.activities.map((activity) => {
                          const completed = isActivityCompleted(activity.id)
                          return (
                            <div key={activity.id} className="flex items-start gap-2 text-sm">
                              {completed ? (
                                <CheckCircle2 className="h-4 w-4 text-success flex-shrink-0 mt-0.5" />
                              ) : (
                                <Circle className="h-4 w-4 text-text-muted flex-shrink-0 mt-0.5" />
                              )}
                              <span className={cn(completed && 'text-text-muted line-through')}>
                                {activity.name}
                                {activity.isRequired && <span className="text-error ml-1">*</span>}
                              </span>
                            </div>
                          )
                        })}
                      </div>
                    </div>

                    {/* Artifacts */}
                    <div>
                      <h4 className="text-xs font-medium text-text-muted uppercase tracking-wide mb-2">
                        Artifacts ({phase.artifacts.length})
                      </h4>
                      <div className="space-y-2">
                        {phase.artifacts.map((artifact) => {
                          const completed = isArtifactCompleted(artifact.id)
                          const Icon = artifact.type === 'canvas' ? Box : FileText
                          return (
                            <div key={artifact.id} className="flex items-start gap-2 text-sm">
                              <Icon
                                className={cn(
                                  'h-4 w-4 flex-shrink-0 mt-0.5',
                                  completed ? 'text-success' : 'text-text-muted'
                                )}
                              />
                              <span className={cn(completed && 'text-text-muted line-through')}>
                                {artifact.name}
                                {artifact.isRequired && <span className="text-error ml-1">*</span>}
                              </span>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Gate */}
                  {phase.gate && (
                    <div className="mt-4 pt-4 border-t border-warm-200">
                      <h4 className="text-xs font-medium text-text-muted uppercase tracking-wide mb-2">
                        Gate: {phase.gate.name}
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {phase.gate.criteria.map((criterion, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {criterion}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="flex items-center justify-center gap-4 pt-4">
          <button
            onClick={() => setActiveMode('canvas')}
            className="px-4 py-2 text-sm font-medium text-warm-700 bg-white border border-warm-200 rounded-lg hover:bg-warm-50 transition-colors"
          >
            Open Canvas
          </button>
          <button
            onClick={() => setActiveMode('entities')}
            className="px-4 py-2 text-sm font-medium text-warm-700 bg-white border border-warm-200 rounded-lg hover:bg-warm-50 transition-colors"
          >
            View Entities
          </button>
        </div>
      </div>
    </div>
  )
}

// Three concentric rings for Market, Solution, Business domains
function DomainHealthRings({
  market,
  solution,
  business,
  overall,
}: {
  market: number
  solution: number
  business: number
  overall: number
}) {
  // Ring configuration: outer to inner (scaled up)
  const rings = [
    { value: market, color: 'var(--color-market)', radius: 72, strokeWidth: 14 },
    { value: solution, color: 'var(--color-solution)', radius: 54, strokeWidth: 14 },
    { value: business, color: 'var(--color-business)', radius: 36, strokeWidth: 14 },
  ]

  return (
    <div className="relative flex-shrink-0">
      <svg className="w-48 h-48" viewBox="0 0 160 160">
        {rings.map((ring, idx) => {
          const circumference = 2 * Math.PI * ring.radius
          const strokeDasharray = `${(ring.value / 100) * circumference} ${circumference}`
          return (
            <g key={idx}>
              {/* Background ring */}
              <circle
                cx="80"
                cy="80"
                r={ring.radius}
                fill="none"
                stroke="var(--color-warm-200)"
                strokeWidth={ring.strokeWidth}
              />
              {/* Progress ring */}
              <circle
                cx="80"
                cy="80"
                r={ring.radius}
                fill="none"
                stroke={ring.color}
                strokeWidth={ring.strokeWidth}
                strokeLinecap="round"
                strokeDasharray={strokeDasharray}
                transform="rotate(-90 80 80)"
                style={{ transition: 'stroke-dasharray 0.6s ease-in-out' }}
              />
            </g>
          )
        })}
      </svg>
      {/* Center text */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold text-warm-900">{Math.round(overall)}%</span>
        <span className="text-xs text-text-muted uppercase tracking-wide">PMF Score</span>
      </div>
    </div>
  )
}

// Domain legend item
function DomainLegendItem({ color, label, value }: { color: string; label: string; value: number }) {
  return (
    <div className="flex items-center gap-2">
      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
      <span className="text-xs text-text-secondary">{label}</span>
      <span className="text-xs font-medium text-warm-900">({Math.round(value)}%)</span>
    </div>
  )
}

// Stat item component (compact version for 2-column grid)
function StatItem({ icon, label, value }: { icon: React.ReactNode; label: string; value: string | number }) {
  return (
    <div className="text-center">
      <div className="w-10 h-10 rounded-lg bg-warm-100 flex items-center justify-center text-text-secondary mx-auto mb-2">
        {icon}
      </div>
      <div className="text-xl font-semibold text-warm-900">{value}</div>
      <div className="text-xs text-text-muted">{label}</div>
    </div>
  )
}

// Domain colors for entity badges
const DOMAIN_COLORS: Record<OntologyDomain, { bg: string; text: string; border: string }> = {
  core: { bg: '#fef7e6', text: '#8a6914', border: '#d4a012' },
  market: { bg: '#e8f2ea', text: '#456b52', border: '#5c8a6b' },
  business: { bg: '#e6f3f7', text: '#2d5a66', border: '#3d7a8c' },
  solution: { bg: '#faf5e8', text: '#9a7f3f', border: '#c9a857' },
}

const DOMAIN_LABELS: Record<OntologyDomain, string> = {
  core: 'Core',
  market: 'Market',
  business: 'Business',
  solution: 'Solution',
}

// Domain entity count badge
function DomainEntityBadge({ domain, count }: { domain: OntologyDomain; count: number }) {
  const colors = DOMAIN_COLORS[domain]
  const isEmpty = count === 0

  return (
    <div
      className="flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium"
      style={{
        backgroundColor: isEmpty ? 'var(--color-warm-100)' : colors.bg,
        color: isEmpty ? 'var(--color-text-muted)' : colors.text,
        border: `1px solid ${isEmpty ? 'var(--color-border-subtle)' : colors.border}40`,
      }}
    >
      <span>{DOMAIN_LABELS[domain]}</span>
      <span className="font-semibold">{count}</span>
    </div>
  )
}

// Attention card component
function AttentionCard({ type, message, compact }: { type: 'critical' | 'warning' | 'info'; message: string; compact?: boolean }) {
  const config = {
    critical: { bg: '#FEE7E9', border: '#F5B8BD', icon: AlertCircle, iconColor: 'var(--color-error)' },
    warning: { bg: '#FEF3E7', border: '#F5D9B8', icon: AlertTriangle, iconColor: 'var(--color-solution)' },
    info: { bg: 'var(--color-warm-50)', border: 'var(--color-border)', icon: Info, iconColor: 'var(--color-business)' },
  }[type]

  const Icon = config.icon

  return (
    <div
      className={cn(
        'flex items-start gap-2 rounded-lg',
        compact ? 'p-2' : 'p-4'
      )}
      style={{ backgroundColor: config.bg, border: `1px solid ${config.border}` }}
    >
      <Icon size={compact ? 14 : 18} style={{ color: config.iconColor }} className="flex-shrink-0 mt-0.5" />
      <span className={cn('text-warm-800', compact ? 'text-xs' : 'text-sm')}>{message}</span>
    </div>
  )
}

// Helper: Get health color based on score
function getHealthColor(health: number): string {
  if (health >= 91) return 'var(--color-business)'
  if (health >= 71) return 'var(--color-success)'
  if (health >= 51) return 'var(--color-solution)'
  if (health >= 31) return '#D4A574'
  return 'var(--color-error)'
}

// Helper: Derive attention points from state
function getAttentionPoints(
  overallProgress: number,
  currentPhaseId: string,
  completedActivities: string[],
  phaseProgress: Record<string, number> = {}
): AttentionPoint[] {
  const points: AttentionPoint[] = []

  if (overallProgress < 20 && overallProgress > 0) {
    points.push({ type: 'critical', message: 'Product health is critically low' })
  }

  if (!currentPhaseId && completedActivities.length === 0 && overallProgress === 0) {
    points.push({ type: 'info', message: 'Start your discovery journey by selecting a goal' })
  }

  const currentProgress = phaseProgress[currentPhaseId] ?? 0
  if (currentPhaseId && currentProgress > 0 && currentProgress < 50) {
    points.push({ type: 'warning', message: 'Current phase needs more progress' })
  }

  if (currentPhaseId && currentProgress >= 90) {
    points.push({ type: 'info', message: 'Ready to advance to next phase' })
  }

  return points
}
